/**
 * The scope the device's data was fetched under (spec 15.4, shared devices). The device
 * keeps one database per browser profile, so what it holds belongs to whoever signed in
 * last: `sync_state[LINGKUP_DATA_KEY]` records the class of that role and the place that
 * fixes its rows (spec 9.4), and every change of signed-in user compares the newcomer's
 * scope with it (./perangkatBersama, ../auth/gantiPengguna). Two users with the same scope
 * see the same rows; any other change clears the device and downloads it again.
 *
 * Pure and platform-neutral: the worker, the session and the tests share it. A new role is
 * added in `kelasLingkup` and `lingkupDataDari` only; the compiler points at both through the
 * exhaustive switches (plan 3's ketua stasi came in that way, as the `stasi` class).
 */
import type { Peran } from '@satu/shared'

/** `sync_state` key of the recorded scope (JSON `LingkupData`). */
export const LINGKUP_DATA_KEY = 'lingkup_data'

/**
 * keuskupan: the admin keuskupan (everything); uskup: the uskup (everything but a person's
 * sensitive fields, `umat_profil`: release 19, so a device the admin used is cleared before the
 * uskup's session reaches it); dekenat: a dekan with the deanery;
 * paroki: admin paroki and staf paroki (one parish, the same rows); lingkungan: a ketua
 * lingkungan with the lingkungan; stasi: a ketua stasi with the parish and the stasi (the
 * lingkungan under the stasi, spec 2026-09-30, 7); pastor: a Pastor Paroki with the parish and,
 * when he is also Ketua Wilayah, the wilayah (release 32: his parish's staff-only fields, and the
 * wilayah's other parishes, so never the same rows as the parish's staff).
 */
export type KelasLingkup =
  | 'keuskupan'
  | 'uskup'
  | 'dekenat'
  | 'paroki'
  | 'pastor'
  | 'lingkungan'
  | 'stasi'

export const KELAS_LINGKUP: readonly KelasLingkup[] = [
  'keuskupan',
  'uskup',
  'dekenat',
  'paroki',
  'pastor',
  'lingkungan',
  'stasi',
]

export interface LingkupData {
  kelas: KelasLingkup
  paroki_id: string | null
  lingkungan_id: string | null
  dekenat_id: string | null
  /**
   * A ketua stasi's stasi; null for every other class. Null for a ketua stasi too means no
   * usable stasi, for which the server sends nothing at all (not "unknown", unlike a dekan's
   * deanery): the session row always carries it.
   */
  stasi_id: string | null
}

/** What a user brings to the comparison: the session row or the /sesi profile. */
export interface PenggunaLingkup {
  peran: Peran
  paroki_id: string | null
  lingkungan_id: string | null
  /**
   * A dekan's deanery, or a Pastor Paroki's wilayah; unknown (absent) on a session row, which then
   * matches no dekan.
   */
  dekenat_id?: string | null | undefined
  /** A ketua stasi's stasi (the session row and the /sesi profile carry it). */
  stasi_id?: string | null | undefined
}

export function kelasLingkup(peran: Peran): KelasLingkup {
  switch (peran) {
    case 'admin_keuskupan':
      return 'keuskupan'
    case 'uskup':
      return 'uskup'
    case 'dekan':
      return 'dekenat'
    case 'admin_paroki':
    case 'staf_paroki':
      return 'paroki'
    case 'pastor_paroki':
      return 'pastor'
    case 'ketua_lingkungan':
      return 'lingkungan'
    case 'ketua_stasi':
      return 'stasi'
  }
}

/** The scope of a user's data: only the place that fixes the rows is kept. */
export function lingkupDataDari(pengguna: PenggunaLingkup): LingkupData {
  const kelas = kelasLingkup(pengguna.peran)
  const kosong: LingkupData = {
    kelas,
    paroki_id: null,
    lingkungan_id: null,
    dekenat_id: null,
    stasi_id: null,
  }
  switch (kelas) {
    case 'keuskupan':
    case 'uskup':
      return kosong
    case 'dekenat':
      return { ...kosong, dekenat_id: pengguna.dekenat_id ?? null }
    case 'paroki':
      return { ...kosong, paroki_id: pengguna.paroki_id }
    case 'pastor':
      return { ...kosong, paroki_id: pengguna.paroki_id, dekenat_id: pengguna.dekenat_id ?? null }
    case 'lingkungan':
      return { ...kosong, paroki_id: pengguna.paroki_id, lingkungan_id: pengguna.lingkungan_id }
    case 'stasi':
      return { ...kosong, paroki_id: pengguna.paroki_id, stasi_id: pengguna.stasi_id ?? null }
  }
}

/**
 * Do two scopes hold the same rows? A dekan whose deanery is unknown matches no one, so a
 * device that cannot tell is cleared rather than shown to the wrong deanery.
 */
export function samaLingkup(a: LingkupData, b: LingkupData): boolean {
  if (a.kelas !== b.kelas) return false
  if (a.kelas === 'dekenat' && (a.dekenat_id === null || b.dekenat_id === null)) return false
  return (
    a.paroki_id === b.paroki_id &&
    a.lingkungan_id === b.lingkungan_id &&
    a.dekenat_id === b.dekenat_id &&
    a.stasi_id === b.stasi_id
  )
}

/**
 * The active user's own check at start and unlock: their session row knows no deanery, so
 * a dekan matches the deanery recorded at their sign-in, and a Pastor Paroki of the same parish
 * the wilayah recorded at his (release 32); otherwise as `samaLingkup`.
 */
export function samaUntukPenggunaAktif(tercatat: LingkupData, pengguna: LingkupData): boolean {
  if (samaLingkup(tercatat, pengguna)) return true
  if (tercatat.kelas === 'pastor' && pengguna.kelas === 'pastor') {
    return pengguna.dekenat_id === null && tercatat.paroki_id === pengguna.paroki_id
  }
  return (
    tercatat.kelas === 'dekenat' && pengguna.kelas === 'dekenat' && pengguna.dekenat_id === null
  )
}

/**
 * The server check of the signed-in person's role and place (../auth/session `periksaPeran`):
 * the same scope exactly when every field is. Unlike `samaLingkup`, a dekan the server knows
 * without a deanery matches the same record, so the check never clears such a device again at
 * every run; the sign-in still clears it (`samaLingkup`), since a sign-in cannot tell.
 */
export function lingkupIdentik(a: LingkupData, b: LingkupData): boolean {
  return (
    a.kelas === b.kelas &&
    a.paroki_id === b.paroki_id &&
    a.lingkungan_id === b.lingkungan_id &&
    a.dekenat_id === b.dekenat_id &&
    a.stasi_id === b.stasi_id
  )
}

function idAtauNull(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

/**
 * The stored scope, or null when absent or not whole (then the device cannot tell). A record
 * written before the stasi class (no `stasi_id`) names no stasi.
 */
export function bacaLingkupData(nilai: unknown): LingkupData | null {
  if (typeof nilai !== 'object' || nilai === null) return null
  const n = nilai as Record<string, unknown>
  if (!KELAS_LINGKUP.includes(n.kelas as KelasLingkup)) return null
  if (!idAtauNull(n.paroki_id) || !idAtauNull(n.lingkungan_id) || !idAtauNull(n.dekenat_id)) {
    return null
  }
  const stasi = n.stasi_id === undefined ? null : n.stasi_id
  if (!idAtauNull(stasi)) return null
  return {
    kelas: n.kelas as KelasLingkup,
    paroki_id: n.paroki_id,
    lingkungan_id: n.lingkungan_id,
    dekenat_id: n.dekenat_id,
    stasi_id: stasi,
  }
}
